import { useMemo, useState } from "react";
import sift from "sift";
import { Accordion } from "../Accordion/Accordion";
import { CardElementAccessories } from "../CardElement/CardElementAccessories";
import { ProductDetailsModal } from "../Modal/ProductDetailsModal";
import { AxysLevelTabs } from "../Tabs/AxysLevelTabs";
import "./styles.scss";

const itemsCache = [];
export const AccessoriesScreen = ({ componentsStateObj, itemsStateObj }) => {
  const [show, setShow] = useState(false);
  const [modalDetails, setModalDetails] = useState({});
  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);

  let defaultOpenAccordion = itemsStateObj.state.activeIds.desk_model._id.includes("mojo_dome");

  const isMonitorActive = useMemo(
    () => itemsStateObj.state.activeIds.accessories.array.find((activeItem) => activeItem._id.includes("monitor")),
    [itemsStateObj.state.activeIds.accessories.array]
  );

  // Find the active Axys level
  const activeAxysLevel = useMemo(() => {
    return itemsStateObj.state.activeIds.accessories.array.find((item) => item._id?.includes("axys_level") || item._id === "no_axys_level");
  }, [itemsStateObj.state.activeIds.accessories.array]);

  // Get the Axys level item object
  const activeAxysLevelObj = useMemo(() => {
    if (!activeAxysLevel) return null;
    return itemsStateObj.state.array.find((item) => item._id === activeAxysLevel._id);
  }, [activeAxysLevel, itemsStateObj.state.array]);

  // Synchronize the included accessory IDs from the active Axys level with the active accessories in state
  useMemo(() => {
    if (activeAxysLevelObj?.includedAccessories?.length > 0) {
      // Check if all accessories from the Axys level are in the active state
      const missingAccessories = activeAxysLevelObj.includedAccessories.filter(
        (accessoryId) => !itemsStateObj.state.activeIds.accessories.array.some((item) => item._id === accessoryId)
      );

      // If there are missing accessories, add them to the state
      if (missingAccessories.length > 0) {
        const copyActiveIds = { ...itemsStateObj.state.activeIds };
        missingAccessories.forEach((accessoryId) => {
          // Find the full accessory object to update its metadata
          const accessoryObj = itemsStateObj.state.array.find((item) => item._id === accessoryId);

          if (!accessoryObj) {
            console.error(`Accessory with ID ${accessoryId} not found in items array`);
            return;
          }

          // Ensure the accessory has the necessary metadata
          if (!accessoryObj.metaData) {
            accessoryObj.metaData = {};
          }

          // Set special flags for included accessories to ensure they load in 3D model
          accessoryObj.metaData.isAxysIncluded = true;
          accessoryObj.metaData.is3dModelDynamicallyInjected = true;

          // Add only the accessory ID to the array, not the complete object
          copyActiveIds.accessories.array.push({
            _id: accessoryId,
          });
        });
        itemsStateObj.setState(copyActiveIds);
      }
    }
  }, [activeAxysLevelObj, itemsStateObj]);

  const getItems = (section) => {
    // check if query is already cached
    if (itemsCache[itemsStateObj.state.activeIds.desk_model]) {
      return itemsCache[itemsStateObj.state.activeIds.desk_model._id][section._id];
    }

    // query is not cached, so fetch it, sort it based off of component.items and add it to the cache
    itemsCache[itemsStateObj.state.activeIds.desk_model._id] = {
      ...itemsCache[itemsStateObj.state.activeIds.desk_model._id],
      ...{
        [section._id]: itemsStateObj.state.array
          .filter(
            sift({
              _id: { $in: componentsStateObj.state.activeObj.items },
              section: { $eq: section._id },
              $or: [{ excluded: { $exists: false } }, { excluded: false }],
            })
          )
          .sort((a, b) => {
            return componentsStateObj.state.activeObj.items?.indexOf(a._id) - componentsStateObj.state.activeObj.items?.indexOf(b._id);
          }),
      },
    };
    return itemsCache[itemsStateObj.state.activeIds.desk_model._id][section._id];
  };

  // Get axys level tabs items
  const getAxysLevelItems = () => {
    const axysItems = itemsStateObj.state.array.filter(
      sift({
        section: { $eq: "axys_packages" },
        $or: [{ excluded: { $exists: false } }, { excluded: false }],
      })
    );

    // Sort to ensure no_axys_level is always first
    return axysItems.sort((a, b) => {
      if (a._id === "no_axys_level") return -1;
      if (b._id === "no_axys_level") return 1;
      return 0;
    });
  };

  // Get included accessories based on the active Axys level
  const getAxysIncludedAccessories = () => {
    if (!activeAxysLevelObj || !activeAxysLevelObj.includedAccessories) return [];

    // Get all accessories in the selected package
    const includedAccessories = itemsStateObj.state.array.filter(
      sift({
        _id: { $in: activeAxysLevelObj.includedAccessories },
        $or: [{ excluded: { $exists: false } }, { excluded: false }],
      })
    );

    // Sort the accessories based on the order in includedAccessories array
    return includedAccessories.sort((a, b) => {
      return activeAxysLevelObj.includedAccessories.indexOf(a._id) - activeAxysLevelObj.includedAccessories.indexOf(b._id);
    });
  };

  // Get all accessories IDs from all Axys levels
  const getAllAxysLevelAccessoryIds = () => {
    const allAxysLevelItems = getAxysLevelItems();
    const allAxysLevelAccessories = [];

    allAxysLevelItems.forEach((axysLevel) => {
      if (axysLevel.includedAccessories) {
        allAxysLevelAccessories.push(...axysLevel.includedAccessories);
      }
    });

    return allAxysLevelAccessories;
  };

  // Get included accessories (those that should be shown in the Included Accessories section)
  const getIncludedAccessories = () => {
    // Get all accessories that should be included based on their current state
    const includedAccessories = itemsStateObj.state.array.filter(
      (item) =>
        item.section === "desk_accessories" &&
        // Must not be excluded
        (!item.excluded || item.excluded === false) &&
        // Must be marked as included
        item.metaData?.isIncluded &&
        // Don't include items that are part of Axys packages
        !getAllAxysLevelAccessoryIds().includes(item._id)
    );

    // Simple prioritization: rollervision first, then PC mount, then everything else
    return includedAccessories.sort((a, b) => {
      if (a._id.includes("rollervision")) return -1;
      if (b._id.includes("rollervision")) return 1;
      if (a._id.includes("cpu_hanger")) return -1;
      if (b._id.includes("cpu_hanger")) return 1;
      if (a._id === "ten_outlet_powerbar") return 1;
      if (b._id === "ten_outlet_powerbar") return -1;
      return 0;
    });
  };

  // Get additional accessories (not included in any Axys level and not marked as included)
  const getAdditionalAccessories = () => {
    // Get the included accessories first
    const includedAccessories = getIncludedAccessories();
    const includedAccessoryIds = includedAccessories.map((item) => item._id);

    // Filter accessories that:
    // 1. Are not excluded
    // 2. Are not in Axys levels
    // 3. Are not marked as included
    // 4. Are not already in included accessories
    const additionalAccessories = itemsStateObj.state.array.filter(
      (item) =>
        item.section === "desk_accessories" &&
        // Must not be excluded
        (!item.excluded || item.excluded === false) &&
        // Must not be in Axys packages
        !getAllAxysLevelAccessoryIds().includes(item._id) &&
        // Must not be marked as included
        (!item.metaData || item.metaData.isIncluded !== true) &&
        // Must not be in included accessories
        !includedAccessoryIds.includes(item._id) &&
        // must not be bias or arc lighting (this is needed on XM6 where these aren't in the AXYS section)
        !item._id.includes("bias_lighting") &&
        !item._id.includes("arc_lighting")
    );

    // Rearrange to prioritize sound dampening panel and CPU mount
    const prioritizedAccessories = [];

    // First, find the sound dampening panel and CPU mount
    const soundDampeningPanel = additionalAccessories.find((item) => item._id === "sound_dampening_panel");
    const cpuMount = additionalAccessories.find((item) => item._id === "cpu_hanger");

    // Add them first if they exist
    if (soundDampeningPanel) {
      prioritizedAccessories.push(soundDampeningPanel);
    }

    if (cpuMount) {
      prioritizedAccessories.push(cpuMount);
    }

    // Add all other accessories
    additionalAccessories.forEach((item) => {
      if (item._id !== "sound_dampening_panel" && item._id !== "cpu_hanger" && item._id !== "cpu_hanger_included") {
        prioritizedAccessories.push(item);
      }
    });

    return prioritizedAccessories;
  };

  // Render included accessories section
  const renderIncludedAccessories = () => {
    const includedAccessories = getIncludedAccessories();

    if (!includedAccessories || includedAccessories.length === 0) {
      return null;
    }

    // Use Accordion component for included accessories
    return (
      <Accordion key="included_accessories" displayName="Included Accessories" _id="included_accessories" defaultOpen={true}>
        <div className="info-text d-flex justify-content-center align-items-center mb-3">
          <img src="/images/iconInfo.svg" alt="info icon" />
          <p className="ms-1 mb-0" style={{ color: "#4F4F4F" }}>
            These items are included with your desk
          </p>
        </div>
        {includedAccessories && includedAccessories.length > 0 ? (
          <CardElementAccessories itemsStateObj={itemsStateObj} accessoriesArray={includedAccessories} cardPerRow={2} isInAxysPackage={false} />
        ) : (
          <div className="text-center">
            <p>No included accessories found. Make sure accessories have the isIncluded flag set to true.</p>
          </div>
        )}
      </Accordion>
    );
  };

  const handleSeeProductDetailsClick = (_modalDetails) => {
    setModalDetails({ ..._modalDetails });
    handleShowModal();
  };

  return (
    <div className="mt-3">
      {componentsStateObj.state.activeObj.sections
        .slice() // Create a copy of the array to avoid mutating the original
        .sort((a, b) => {
          // Custom sorting function to order sections
          if (a._id === "monitors") return -1; // Monitors first
          if (b._id === "monitors") return 1;
          if (a._id === "axys_packages") return -1; // Axys packages second
          if (b._id === "axys_packages") return 1;
          return 0; // Keep original order for other sections
        })
        .map((section, index) => {
          // Handle Axys Packages section
          if (section._id === "axys_packages") {
            const axysLevelItems = getAxysLevelItems();

            return (
              <Accordion key={section._id} displayName={section.displayName} _id={section._id} defaultOpen={defaultOpenAccordion}>
                {/* Info text with more info button */}
                <div className="info-text d-flex flex-column align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <img src="/images/iconInfo.svg" alt="info icon" />
                    <p className="ms-1 mb-0" style={{ color: "#4F4F4F" }}>
                      Select level of climate control, lighting
                    </p>
                  </div>
                  <div className="d-flex justify-content-center mt-2">
                    <button
                      id="modalButton"
                      onClick={() =>
                        handleSeeProductDetailsClick({
                          title: "AXYS Climate Control & Lighting",
                          imageSrc: "/images/accessories/axys.png",
                        })
                      }
                    >
                      more info
                    </button>
                  </div>
                </div>

                {/* Axys Video */}
                <div className="d-flex justify-content-center mb-3">
                  <video
                    src="https://cdn.shopify.com/videos/c/o/v/c8a2de935a974ee09906b991ba5a4d04.mp4"
                    controls
                    className="img-fluid"
                    style={{ maxWidth: "100%" }}
                    muted
                    loop
                    playsInline
                  />
                </div>

                {/* Axys Level Tabs */}
                <div className="mb-3">
                  <AxysLevelTabs tabs={axysLevelItems} itemsStateObj={itemsStateObj} />
                </div>

                {/* Display Axys price */}
                {activeAxysLevelObj && activeAxysLevelObj.price !== undefined && (
                  <div className="axys-price-container text-center mb-3">
                    <h4 className="axys-price mb-0">
                      ${parseFloat(activeAxysLevelObj.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </h4>
                  </div>
                )}

                {/* Display Axys included accessories in the accordion */}
                {activeAxysLevelObj && activeAxysLevelObj.includedAccessories && activeAxysLevelObj.includedAccessories.length > 0 && (
                  <div className="mb-3">
                    <CardElementAccessories
                      itemsStateObj={itemsStateObj}
                      accessoriesArray={getAxysIncludedAccessories()}
                      cardPerRow={2}
                      isInAxysPackage={true}
                    />
                  </div>
                )}
              </Accordion>
            );
          }

          // Hide the desk_accessories section since we're using additional_accessories instead
          if (section._id === "desk_accessories") {
            const additionalAccessories = getAdditionalAccessories();

            if (additionalAccessories.length === 0) return null;

            return (
              <Accordion key={section._id} displayName={"Additional Accessories"} _id={section._id} defaultOpen={defaultOpenAccordion}>
                {section?.description && (
                  <div className="info-text d-flex justify-content-center align-items-center mb-3">
                    <img src="/images/iconInfo.svg" alt="info icon" />
                    <h6 className="ms-1 mb-0" style={{ color: "#4F4F4F" }}>
                      {section.description}
                    </h6>
                  </div>
                )}
                <CardElementAccessories itemsStateObj={itemsStateObj} accessoriesArray={additionalAccessories} cardPerRow={2} />
              </Accordion>
            );
          }

          // For other sections (monitors)
          return (
            <Accordion key={section._id} displayName={section.displayName} _id={section._id} defaultOpen={defaultOpenAccordion}>
              {section?.description && (
                <div className="info-text mb-3">
                  {!(
                    itemsStateObj.state.activeObjs.desk_model?._id.includes("mojo_dome") ||
                    itemsStateObj.state.activeObjs.desk_model?._id.includes("battle_station")
                  ) && (
                    <div className="d-flex justify-content-center align-items-center">
                      <img src="/images/iconInfo.svg" alt="info icon" />
                      <h6 className="ms-1 mb-0" style={{ color: "#4F4F4F" }}>
                        {section.description}
                      </h6>
                    </div>
                  )}
                  {section?.subDescription && isMonitorActive && (
                    <div className="d-flex justify-content-center align-items-center">
                      <p className="ms-1 mb-0" style={{ color: "#4F4F4F" }}>
                        {section.subDescription}
                      </p>
                    </div>
                  )}
                  {itemsStateObj.state.activeIds.desk_model._id.includes("mojo_dome") && (
                    <div className="d-flex justify-content-center align-items-center">
                      <p className="ms-1 mb-0 text-center" style={{ color: "#4F4F4F" }}>
                        Desktop Fits One 32" Screen or Two 24" Screens. Does not support monitor arms.
                      </p>
                    </div>
                  )}
                  {section._id === "desk_accessories" && itemsStateObj.state.activeIds.desk_model._id.includes("battle_station") && (
                    <div className="d-flex justify-content-center align-items-center">
                      <img src="/images/iconInfo.svg" alt="info icon" />
                      <p className="ms-1 mb-0 text-center" style={{ color: "#4F4F4F" }}>
                        The following accessories are included in the bundle
                      </p>
                    </div>
                  )}
                </div>
              )}
              {/* accessory cards include monitors and general desk accessories */}
              <CardElementAccessories itemsStateObj={itemsStateObj} accessoriesArray={getItems(section)} cardPerRow={2} />
            </Accordion>
          );
        })}

      {/* Add divider between additional accessories and included accessories */}
      <hr className="my-4" style={{ borderTop: "2px solid #b0b0b0", width: "80%", margin: "0 auto" }} />

      {/* Render included accessories outside of accordions */}
      {renderIncludedAccessories()}

      <ProductDetailsModal show={show} modalDetails={modalDetails} onHide={handleCloseModal} />
    </div>
  );
};
